import {useCallback, useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {messageFor} from "../../api/dependents";
import {leaveDependent} from "./leaveDependent";

// Loading one section of a dependent's record.
//
// Three things every dependent screen needs and none of them should re-invent:
// who is active, whether this section is offered at all, and the load itself.
//
// The section check reads `sections` from the SERVER's answer. It is not a
// permission check — the endpoint performs that, on every request, and would
// refuse regardless. It exists so the app can say "this is not available to
// you" instead of drawing an empty screen that looks like a dependent with no
// appointments, no forms and no balance.

export const SECTION_STATES = {
  LOADING: "loading",
  UNAVAILABLE: "unavailable",
  // The link itself is gone. Kept apart from UNAVAILABLE because the two send
  // the reader somewhere different: "this section is closed to you" invites a
  // call asking for it, and "your access to this person has ended" is not a
  // request anybody can make of the front desk. Saying the first when the
  // second is true is the kind of wrong that produces the wrong phone call.
  REVOKED: "revoked",
  ERROR: "error",
  READY: "ready",
};

/**
 * @param {object}   args.state      the app state (carries `jwt` and `activeDependent`)
 * @param {string}   args.section    one of the server's section names
 * @param {Function} args.load       (state, dependentPatientId) => Promise<data>
 */
export function useDependentSection({state, section, load}) {
  const navigate = useNavigate();
  const dependent = state?.activeDependent || null;
  const dependentPatientId = dependent?.dependentPatientId || null;
  const offered = !!dependent?.sections?.includes(section);

  const [status, setStatus] = useState(
    !dependentPatientId || !offered ? SECTION_STATES.UNAVAILABLE : SECTION_STATES.LOADING
  );
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    if (!dependentPatientId || !offered) {
      setStatus(SECTION_STATES.UNAVAILABLE);
      return;
    }
    setStatus(SECTION_STATES.LOADING);
    try {
      const result = await load(state, dependentPatientId);
      setData(result);
      setStatus(SECTION_STATES.READY);
    } catch (err) {
      // A 404 here means "not yours" — a revoked link, or an old tab left open
      // on a guardianship that has since ended. Not a fault of the guardian's,
      // so it is not an error; but it is not "this section is closed" either,
      // and it used to be told as if it were.
      if (err?.response?.status === 404) {
        setStatus(SECTION_STATES.REVOKED);
        return;
      }
      setError(messageFor(err, "This could not be loaded. Please try again."));
      setStatus(SECTION_STATES.ERROR);
    }
    // `load` is defined inline by every caller, so depending on it would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dependentPatientId, offered, state?.jwt]);

  useEffect(() => {
    reload();
  }, [reload]);

  return {
    status,
    data,
    error,
    reload,
    // The way out, for a guardian whose access ended while they were looking at
    // it. Offered by the hook rather than rebuilt on six screens.
    leave: useCallback(() => leaveDependent(navigate, state), [navigate, state]),
    dependent,
    dependentPatientId,
    displayName: nameOf(dependent),
  };
}

export function nameOf(dependent) {
  const identity = dependent?.identity;
  if (!identity) return "this dependent";
  return `${identity.fName || ""} ${identity.lName || ""}`.trim() || "this dependent";
}
